import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";

import {
  createDocumentTypeAction,
  updateDocumentTypeAction,
} from "../../Actions/DocumentTypeActions";

import { MyCloseIcon } from "../../Assets/svg/MyIcons";


const getChangedPlanFields = (oldData, newData) => {
  const changes = {};
  if (!oldData) return changes;

  Object.keys(newData).forEach((key) => {
    const oldVal = oldData[key];
    const newVal = newData[key];

    const oldIsEmpty = oldVal === null || oldVal === undefined || oldVal === "";
    const newIsEmpty = newVal === null || newVal === undefined || newVal === "";

    if (oldIsEmpty && newIsEmpty) return;

    if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
      changes[key] = {
        old: oldVal,
        new: newVal,
      };
    }
  });

  return changes;
};

const AddEditDocumentType = ({
  open,
  handleClose,
  editData,
  setSnackbar,
  refresh,
}) => {
  const [loading, setLoading] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState(null);
  const [changedFields, setChangedFields] = useState({});

  const formik = useFormik({
    initialValues: {
      code: editData?.code || "",
      name: editData?.name || "",
      provider: editData?.provider || "SUMSUB",
      provider_code: editData?.provider_code || "",
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      code: yup.string().trim().required("Code is required"),
      name: yup.string().trim().required("Name is required"),
      provider_code: yup.string().trim().required("Provider code is required"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const payload = { ...values };
        if (editData) {
          payload.changedFields = JSON.stringify(Object.keys(changedFields));
          payload.comparisonData = JSON.stringify(changedFields);
          await updateDocumentTypeAction(editData.id, payload);

          setSnackbar({
            open: true,
            message: "Document type updated successfully",
            severity: "success",
          });

        } else {
          await createDocumentTypeAction(values);

          setSnackbar({
            open: true,
            message: "Document type created successfully",
            severity: "success",
          });
        }

        handleClose();
        refresh();

      } catch (err) {
        setSnackbar({
          open: true,
          message: err?.response?.data?.message,
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    }
  });

  useEffect(() => {
    if (editData) {
      const initialValuesToSet = {
        code: editData?.code || "",
        name: editData?.name || "",
        provider: editData?.provider || "SUMSUB",
        provider_code: editData?.provider_code || "",
      };
      setInitialFormValues(initialValuesToSet);
    }
  }, [editData]);

  useEffect(() => {
    if (!editData || !initialFormValues) return;

    const changed = getChangedPlanFields(initialFormValues, formik.values);
    setChangedFields(changed);
  }, [formik.values, initialFormValues, editData]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth>
      {/* 🔥 HEADER (matches whitelist style) */}
      <DialogTitle className="fw-semibold fs-2 border-bottom">
        {editData ? "Update Document Type" : "Add Document Type"}
      </DialogTitle>

      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 15,
        }}
      >
        <MyCloseIcon />
      </IconButton>

      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <TextField
            label="Code"
            name="code"
            fullWidth
            margin="normal"
            value={formik.values.code}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.code && Boolean(formik.errors.code)}
            helperText={formik.touched.code && formik.errors.code}
          />

          <TextField
            label="Name"
            name="name"
            fullWidth
            margin="normal"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
          />

          <TextField
            label="Provider"
            name="provider"
            fullWidth
            margin="normal"
            value={formik.values.provider}
            onChange={formik.handleChange}
          />
          <TextField
            label="Provider Code"
            name="provider_code"
            fullWidth
            margin="normal"
            value={formik.values.provider_code}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.provider_code && Boolean(formik.errors.provider_code)}
            helperText={formik.touched.provider_code && formik.errors.provider_code}
          />
        </DialogContent>

        {/* 🔥 FOOTER (EXACT whitelist style) */}
        <div className="px-7 py-5 d-flex justify-content-between border-top">
          <button
            type="button"
            className="btn btn-sm btn-outline btn-outline-primary"
            onClick={handleClose}
          >
            Cancel
          </button>

          <button
            type="submit"
            className="btn btn-sm btn-primary"
            disabled={loading}
          >
            {editData ? "Update" : "Add"}

            {loading && (
              <span
                className="spinner-border spinner-border-sm ms-2"
                role="status"
              />
            )}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

export default AddEditDocumentType;